import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  AgreementReadModelSort,
  CreateAgreementPayload,
  PermissionKeys,
  RelationshipType,
  SortOrder,
  useGetAgreementsQuery,
  useGetBuildingsByIdQuery,
  useGetSpacesByIdQuery,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  InfoTooltip,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from '@client/toolkit';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

import { AgreementItem } from './agreement-item.component';

import { useCreateAgreement } from '../../../../hooks';
import { AgreementCreateButton } from '../../../../components/agreement-create-button/agreement-create-button';
import { PortfolioPaths } from '../../portfolio.router';

export interface BuildingAgreementsCardProps {
  className?: string;
  buildingId?: string;
  spaceId?: never;
}

export interface SpaceAgreementsCardProps {
  className?: string;
  spaceId?: string;
  buildingId?: never;
}

export type AgreementsCardProps = BuildingAgreementsCardProps | SpaceAgreementsCardProps;

export function AgreementsCard({
  className,
  spaceId: parentSpaceId,
  buildingId: parentBuildingId,
}: AgreementsCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tAgreements } = useTranslation('agreements');

  const pageSize = 25;
  const [page, setPage] = useState(0);
  const [sortProperty, setSortProperty] = useState<AgreementReadModelSort | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder>('Asc');

  const handleSort = (property: AgreementReadModelSort) => {
    if (sortProperty === property) {
      if (sortOrder === 'Asc') {
        setSortOrder('Desc');
      } else {
        setSortProperty(undefined);
        setSortOrder('Asc');
      }
    } else {
      setSortProperty(property);
      setSortOrder('Asc');
    }
    setPage(0);
  };

  const SortIcon = ({ property }: { property: AgreementReadModelSort }) => {
    if (sortProperty !== property) return <FaSort className="ml-1 inline opacity-30" />;
    return sortOrder === 'Asc' ? <FaSortUp className="ml-1 inline" /> : <FaSortDown className="ml-1 inline" />;
  };

  const {
    data: space,
    isFetching: isFetchingSpace,
    isLoading: isLoadingSpace,
  } = useGetSpacesByIdQuery(
    {
      id: parentSpaceId ?? '',
    },
    { skip: !parentSpaceId },
  );

  const {
    data: building,
    isFetching: isFetchingBuilding,
    isLoading: isLoadingBuilding,
  } = useGetBuildingsByIdQuery(
    {
      id: parentBuildingId ?? '',
    },
    { skip: !parentBuildingId },
  );

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const {
    data: agreementsData,
    isFetching: isFetchingAgreements,
    isLoading: isLoadingAgreements,
  } = useGetAgreementsQuery(
    {
      'spaceAncestorPath.Equal': ancestorPath,
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      'sort.Property': sortProperty,
      'sort.Order': sortProperty ? sortOrder : undefined,
    },
    { skip: !ancestorPath },
  );

  const agreements = useMemo(() => agreementsData?.items ?? [], [agreementsData?.items]);

  const isListEmpty = useMemo(() => agreements != null && agreements.length <= 0, [agreements]);
  const isLoading = isLoadingAgreements || isLoadingBuilding || isLoadingSpace;
  const isFetchingParent = isFetchingBuilding || isFetchingSpace;
  const isFetching = isFetchingAgreements || isFetchingParent;

  const totalCount = agreementsData?.totalCount ?? 0;

  const isBuilding = !!parentBuildingId;
  const isSpace = !!parentSpaceId;

  const navigate = useNavigate();
  const handleClickImportAgreements = () => {
    if (!parentBuildingId) return;
    navigate(generatePath(PortfolioPaths.BuildingAgreementsImport, { buildingId: parentBuildingId }));
  };

  // COMMENTED OUT: Standard leie dependency - uncomment to restore rent requirement
  // const hasSpaceRent = useMemo(() => space?.rent != null, [space?.rent]);

  const { create: createAgreement, isLoading: isCreating } = useCreateAgreement();

  const handleClickCreateAgreement = () => {
    let payload: Partial<CreateAgreementPayload> | undefined;
    let search: string | undefined;

    // COMMENTED OUT: Original logic requiring rent - uncomment to restore
    // if (space && space.rent) {
    //   payload = {
    //     spaces: [
    //       {
    //         spaceId: space.id,
    //         price: space.rent && {
    //           productId: space.rent.product.id,
    //           unitAmount: space.rent.unitAmount,
    //           description: space.rent.description,
    //           id: space.rent.id,
    //           interval: space.rent.interval,
    //           source: space.rent.source,
    //         },
    //       },
    //     ],
    //   };
    // } else if (parentBuildingId) {
    //   search = `buildingId=${parentBuildingId}`;
    // }

    // Current logic: allow creating agreements without rent
    if (space) {
      payload = {
        spaces: [
          space.rent
            ? {
                spaceId: space.id,
                price: {
                  productId: space.rent.product.id,
                  unitAmount: space.rent.unitAmount,
                  description: space.rent.description,
                  id: space.rent.id,
                  interval: space.rent.interval,
                  source: space.rent.source,
                },
              }
            : {
                spaceId: space.id,
              },
        ],
      };
    } else if (parentBuildingId) {
      search = `buildingId=${parentBuildingId}`;
    }

    createAgreement(payload, { path: { search } });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense">
          {tGlobal('space.rentalAgreements')}
        </CardTitle>
        <CardActions>
          {/* COMMENTED OUT: Standard leie dependency - uncomment to restore rent requirement tooltip
          <InfoTooltip
            message={tGlobal('tooltip.spaceIsMissingDefaultRent')}
            disabled={isFetchingParent || hasSpaceRent || !isSpace}
          >
            <Button
              color="primary-dark"
              disabledVariant="dark"
              size="sm"
              disabled={ancestorPath == null || (!hasSpaceRent && isSpace)}
              loading={isFetchingParent}
              progressing={isCreating}
              onClick={handleClickCreateAgreement}
            >
              {tCommon('create')}
            </Button>
          </InfoTooltip>
          */}
          {isBuilding && (
            <AgreementCreateButton
              spaceId={parentBuildingId ?? ''}
              onClick={handleClickImportAgreements}
              title={tAgreements('agreementImport.entryButton')}
              disabled={ancestorPath == null}
            />
          )}
          <AgreementCreateButton
            spaceId={(isBuilding ? parentBuildingId : parentSpaceId) ?? ''}
            onClick={handleClickCreateAgreement}
            title={tCommon('create')}
            disabled={ancestorPath == null}
          />
        </CardActions>
      </CardHeader>
      <CardContent variant="fill">
        <Table className="overflow-hidden rounded-md">
          {!isLoading && !isFetching && isListEmpty && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntityFound', { entity: tGlobal('entity.agreement', { context: 'plural' }) })}
              </EmptyPlaceholder>
            </caption>
          )}

          <TableHead>
            <TableHeadRow>
              <TableHeadCell
                className="cursor-pointer select-none hover:bg-surface-hover"
                onClick={() => handleSort('TenantName')}
              >
                {tType(`RelationshipType.${'Tenant' as RelationshipType}`)}
                <SortIcon property="TenantName" />
              </TableHeadCell>
              <TableHeadCell>{tGlobal('entity.agreementSpace')}</TableHeadCell>
              <TableHeadCell>{tType(`RelationshipType.${'Owner' as RelationshipType}`)}</TableHeadCell>
              <TableHeadCell
                className="cursor-pointer select-none hover:bg-surface-hover"
                onClick={() => handleSort('Name')}
              >
                {tGlobal('agreement.name')}
                <SortIcon property="Name" />
              </TableHeadCell>
              <TableHeadCell
                className="cursor-pointer select-none hover:bg-surface-hover"
                onClick={() => handleSort('Status')}
              >
                {tGlobal('agreement.status')}
                <SortIcon property="Status" />
              </TableHeadCell>
              <TableHeadCell
                className="cursor-pointer select-none hover:bg-surface-hover"
                onClick={() => handleSort('Start')}
              >
                {tGlobal('agreement.durationStart')}
                <SortIcon property="Start" />
              </TableHeadCell>
              <TableHeadCell
                className="cursor-pointer select-none hover:bg-surface-hover"
                onClick={() => handleSort('End')}
              >
                {tGlobal('agreement.durationEnd')}
                <SortIcon property="End" />
              </TableHeadCell>
            </TableHeadRow>
          </TableHead>

          <TableBody>
            {(isLoading || (isFetching && isListEmpty)) &&
              Array.from(Array(3).keys()).map((x) => <AgreementItem key={x} isFetching />)}

            {!isLoading &&
              agreements?.map((agreement, index) => (
                <AgreementItem
                  key={agreement.id}
                  agreement={agreement}
                  isFetching={isFetching}
                  isBuilding={isBuilding}
                  isSpace={isSpace}
                />
              ))}
          </TableBody>
        </Table>

        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
